import unescapedText from 'I18n/utils/unescapedText';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useState, useEffect } from 'react';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { PreloadImageLink } from '../../components/PreloadImageLink';
import { HeroSection } from './HeroSection';
import { VideoOverlay } from './VideoOverlay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CarouselPlayer({
  currentIndex,
  onNavigate,
  totalItems
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    backgroundColor: "fill-surface-default",
    blockSize: "24px",
    borderRadius: "400",
    gap: "200",
    justifyContent: "center",
    paddingBlock: "100",
    children: [/*#__PURE__*/_jsx(IconButton, {
      "aria-label": unescapedText('marketplaceStorefront.heroCarousel.navigation.previousSlide'),
      onClick: () => onNavigate('prev'),
      size: "sm",
      tooltip: unescapedText('marketplaceStorefront.heroCarousel.navigation.previousSlide'),
      use: "transparent",
      children: /*#__PURE__*/_jsx(LeftIcon, {
        color: "icon-core-default",
        size: "400"
      })
    }), /*#__PURE__*/_jsxs(Text, {
      color: "text-core-default",
      typography: "body-100",
      children: [currentIndex + 1, "/", totalItems]
    }), /*#__PURE__*/_jsx(IconButton, {
      "aria-label": unescapedText('marketplaceStorefront.heroCarousel.navigation.nextSlide'),
      onClick: () => onNavigate('next'),
      size: "sm",
      tooltip: unescapedText('marketplaceStorefront.heroCarousel.navigation.nextSlide'),
      use: "transparent",
      children: /*#__PURE__*/_jsx(RightIcon, {
        color: "icon-core-default",
        size: "400"
      })
    })]
  });
}
export function HeroCarousel({
  heroItems
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const totalItems = heroItems.length;
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      md: false
    }
  });

  // Auto-scroll functionality - pauses when hovering or video is playing
  useEffect(() => {
    if (totalItems <= 1 || isHovering || isVideoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === totalItems - 1 ? 0 : prev + 1);
    }, 6000);
    return () => clearInterval(interval);
  }, [totalItems, isHovering, isVideoPlaying]);
  const handleNavigate = direction => {
    const newIndex = (() => {
      const prev = currentIndex;
      if (direction === 'prev') {
        return prev === 0 ? totalItems - 1 : prev - 1;
      }
      return prev === totalItems - 1 ? 0 : prev + 1;
    })();
    trackMarketplaceEvent(getMarketplaceInteractionEvent({
      subscreen: `HERO__${newIndex}`,
      action: 'hero carousel button clicked',
      pageNumber: newIndex
    }));
    setCurrentIndex(newIndex);
  };
  const handleVideoInteraction = isPlaying => {
    setIsVideoPlaying(isPlaying);
  };
  if (totalItems === 0) return null;
  return /*#__PURE__*/_jsxs(View, {
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
    paddingBlockStart: "1100",
    position: "relative",
    children: [heroItems.map((item, index) => item.imageUrl ? /*#__PURE__*/_jsx(PreloadImageLink, {
      href: item.imageUrl
    }, `preload-${index}`) : null), heroItems.map((item, index) => /*#__PURE__*/_jsx(View, {
      "aria-hidden": currentIndex === index ? undefined : true,
      display: currentIndex === index ? 'block' : 'none',
      children: /*#__PURE__*/_jsx(HeroSection, {
        buttonText: item.buttonText,
        buttonUrl: item.buttonUrl,
        heroPosition: index,
        imageUrl: item.imageUrl,
        isActive: currentIndex === index,
        subtitle: item.subtitle,
        title: item.title,
        videoId: item.videoId
      })
    }, `${item.path}-${item.title}-${index}`)), heroItems.map((item, index) => item.videoId ? /*#__PURE__*/_jsx(VideoOverlay, {
      isVisible: currentIndex === index,
      onVideoInteraction: handleVideoInteraction,
      videoId: item.videoId
    }, `video-player-${item.videoId}`) : null), totalItems > 1 && /*#__PURE__*/_jsx(Flex, {
      insetBlockEnd: isMobile ? '-800' : '200',
      insetInlineEnd: "0",
      insetInlineStart: "0",
      justifyContent: "center",
      marginBlockStart: isMobile ? '400' : '0',
      position: isMobile ? 'relative' : 'absolute',
      children: /*#__PURE__*/_jsx(CarouselPlayer, {
        currentIndex: currentIndex,
        onNavigate: handleNavigate,
        totalItems: totalItems
      })
    })]
  });
}